import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import { damp3, dampE } from "maath/easing";

import { PencilLinesPass } from "./PencilLinesPass";
import calendar from "./calendar.json";
import fontJson from "./font.json";
import * as cardsPng from "../static/cards/*.png";
// CNAME import triggers parcel to copy it to dist
import "../static/CNAME";

// =============================================================================
// Types & Interfaces
// =============================================================================

type ViewModeName = "carousel" | "orbit" | "freefly";

interface GameState {
  readonly viewMode: ViewModeName;
  readonly currentCardIndex: number;
  readonly isFlipped: boolean;
  readonly keysPressed: Readonly<KeysPressed>;
  readonly dragOffset: Readonly<{ x: number; y: number }>;
  readonly isDragging: boolean;
}

interface KeysPressed {
  readonly w: boolean;
  readonly a: boolean;
  readonly s: boolean;
  readonly d: boolean;
  readonly q: boolean;
  readonly e: boolean;
  readonly ArrowLeft: boolean;
  readonly ArrowRight: boolean;
  readonly ArrowUp: boolean;
  readonly ArrowDown: boolean;
}

interface RenderState {
  cards: CardRenderData[];
  backgroundCubes: THREE.Mesh[];
  titleGroup: THREE.Group | null;
  hintsGroup: THREE.Group | null;
}

interface CardRenderData {
  mesh: THREE.Mesh;
  startPosition: THREE.Vector3;
}

type Action =
  | { type: "NEXT_CARD" }
  | { type: "PREV_CARD" }
  | { type: "FLIP_CARD" }
  | { type: "UNFLIP_CARD" }
  | { type: "UNSELECT_CARD" }
  | { type: "SWITCH_MODE"; mode: ViewModeName }
  | { type: "KEY_DOWN"; key: string }
  | { type: "KEY_UP"; key: string }
  | { type: "SET_DRAG"; x: number; y: number; isDragging: boolean }
  | { type: "SELECT_CARD"; cardIndex: number };

// =============================================================================
// View Mode Interface (Strategy Pattern)
// =============================================================================

interface ViewMode {
  name: ViewModeName;
  enter(renderState: RenderState, camera: THREE.PerspectiveCamera): void;
  handleInput(gameState: GameState, key: string): Action | null;
  updateCamera(
    gameState: GameState,
    camera: THREE.PerspectiveCamera,
    delta: number
  ): void;
  animateCards(
    gameState: GameState,
    renderState: RenderState,
    camera: THREE.PerspectiveCamera,
    delta: number
  ): void;
  animateTitle(
    gameState: GameState,
    titleGroup: THREE.Group,
    camera: THREE.PerspectiveCamera,
    delta: number
  ): void;
  handleCardClick(gameState: GameState, cardIndex: number): Action | null;
}

// =============================================================================
// Constants
// =============================================================================

// Responsive scale factor - calculated dynamically
function getViewportScale(): number {
  return (window.innerWidth * 0.95) / 1000;
}

// Debug overlay (hidden by default, toggle with 'd')
const debugDiv = document.createElement('div');
debugDiv.style.cssText = 'position:fixed;top:10px;left:10px;background:red;color:white;padding:10px;z-index:9999;font-size:14px;display:none;';
document.body.appendChild(debugDiv);
function updateDebug() {
  const vs = getViewportScale();
  debugDiv.innerHTML = `W: ${window.innerWidth}<br>VS: ${vs.toFixed(3)}<br>Card: ${(3+2*vs).toFixed(3)}`;
  requestAnimationFrame(updateDebug);
}
updateDebug();
window.addEventListener('keydown', (e) => {
  if (e.key === 'd') debugDiv.style.display = debugDiv.style.display === 'none' ? 'block' : 'none';
});
const IS_SMALL_SCREEN = window.innerWidth < 768; // For binary layout decisions
const ORBIT_SPEED = 0.001;
const CAMERA_MOVE_SPEED = 0.1;
const ANIMATION_SMOOTHING = 0.15;
const TOTAL_CARDS = calendar.length;

const BACKGROUND_CUBE_CONFIG = {
  count: 3500,
  outerRadius: 45,
  innerRadius: 12,
};

const MODE_KEYS: Record<string, ViewModeName> = {
  "1": "carousel",
  "2": "orbit",
  "3": "freefly",
};

// Helper to create flip/unflip action based on current state
function createFlipAction(isFlipped: boolean): Action {
  return isFlipped ? { type: "UNFLIP_CARD" } : { type: "FLIP_CARD" };
}

// Calculate ring radius for orbit mode (used by both camera and card animation)
function calculateOrbitRingRadius(): number {
  const cardWidth = 1.1 * 0.6;
  const gap = 0.3;
  const circumference = TOTAL_CARDS * (cardWidth + gap);
  return Math.max(circumference / (2 * Math.PI), 3);
}

// =============================================================================
// Carousel Mode (Option A)
// =============================================================================

const carouselMode: ViewMode = {
  name: "carousel",

  enter(renderState, camera) {
    camera.position.set(0, 0, 6);
    camera.lookAt(0, 0, 0);
  },

  handleInput(gameState, key) {
    if (key === "ArrowLeft") return { type: "PREV_CARD" };
    if (key === "ArrowRight") return { type: "NEXT_CARD" };
    if (key === " " || key === "Enter" || key === "ArrowUp" || key === "ArrowDown") return createFlipAction(gameState.isFlipped);
    return null;
  },

  updateCamera(gameState, camera, delta) {
    // Subtle tilt based on drag
    const tiltX = gameState.dragOffset.y * 0.3;
    const tiltY = gameState.dragOffset.x * 0.3;

    damp3(camera.position, [tiltY * 2, tiltX * 1.5, 6], ANIMATION_SMOOTHING, delta);
    camera.lookAt(0, 0, 0);
  },

  animateCards(gameState, renderState, camera, delta) {
    const { currentCardIndex, isFlipped } = gameState;

    renderState.cards.forEach((card, index) => {
      const offset = index - currentCardIndex;
      const absOffset = Math.abs(offset);

      // Only show current card and immediate neighbors
      if (absOffset > 2) {
        const hideX = offset > 0 ? 15 : -15;
        damp3(card.mesh.position, [hideX, 0, -5], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [0.1, 0.1, 0.1], ANIMATION_SMOOTHING, delta);
        return;
      }

      // Current card: centered, fills most of view
      if (offset === 0) {
        const targetRotY = isFlipped ? Math.PI : 0;
        const cardScale = 3 + 2 * getViewportScale(); // Base + proportional (less severe)
        damp3(card.mesh.position, [0, -1, 0], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [cardScale, cardScale, cardScale], ANIMATION_SMOOTHING, delta);
        dampE(card.mesh.rotation, [0, targetRotY, 0], ANIMATION_SMOOTHING, delta);
        return;
      }

      // Adjacent cards: smaller, to the sides, behind
      const side = offset > 0 ? 1 : -1;
      const targetX = side * (3 + (absOffset - 1) * 1.5);
      const targetZ = -2 - absOffset;
      const targetRotY = side * 0.4;
      const scale = 1.2 - absOffset * 0.3;

      damp3(card.mesh.position, [targetX, -1, targetZ], ANIMATION_SMOOTHING, delta);
      damp3(card.mesh.scale, [scale, scale, scale], ANIMATION_SMOOTHING, delta);
      dampE(card.mesh.rotation, [0, targetRotY, 0], ANIMATION_SMOOTHING, delta);
    });
  },

  animateTitle(gameState, titleGroup, camera, delta) {
    // Title prominent at top of view, slightly back
    const time = Date.now() * 0.001;
    const targetY = 3 + Math.sin(time) * 0.02;

    damp3(titleGroup.position, [0, targetY, -1], ANIMATION_SMOOTHING, delta);
    damp3(titleGroup.scale, [1, 1, 1], ANIMATION_SMOOTHING, delta);
    dampE(titleGroup.rotation, [0, 0, 0], ANIMATION_SMOOTHING, delta);
  },

  handleCardClick(gameState, cardIndex) {
    if (cardIndex === gameState.currentCardIndex) return createFlipAction(gameState.isFlipped);
    return { type: "SELECT_CARD", cardIndex };
  },
};

// =============================================================================
// Orbit Mode (Option B)
// =============================================================================

const orbitMode: ViewMode = {
  name: "orbit",

  enter(renderState, camera) {
    camera.position.set(0, 0, 3.5);
    camera.lookAt(0, 0, 0);
  },

  handleInput(gameState, key) {
    if (key === "ArrowLeft") return { type: "PREV_CARD" };
    if (key === "ArrowRight") return { type: "NEXT_CARD" };
    if (key === " " || key === "Enter" || key === "ArrowUp" || key === "ArrowDown") return createFlipAction(gameState.isFlipped);
    return null;
  },

  updateCamera(gameState, camera, delta) {
    const { currentCardIndex, dragOffset } = gameState;
    const ringRadius = calculateOrbitRingRadius();

    // Camera orbits outside the ring, close to cards
    const angle = (currentCardIndex / TOTAL_CARDS) * Math.PI * 2;
    const cameraRadius = ringRadius + 3 + (2 / getViewportScale()); // Further on small screens

    // Add drag offset for wobble
    const adjustedAngle = angle + dragOffset.x * 0.4;
    const heightOffset = dragOffset.y * 0.8;

    const targetX = Math.sin(adjustedAngle) * cameraRadius;
    const targetZ = Math.cos(adjustedAngle) * cameraRadius;
    const targetY = 0.5 + heightOffset;

    damp3(camera.position, [targetX, targetY, targetZ], ANIMATION_SMOOTHING, delta);

    // Look at center
    camera.lookAt(0, 0, 0);
  },

  animateCards(gameState, renderState, camera, delta) {
    const { currentCardIndex, isFlipped } = gameState;
    const ringRadius = calculateOrbitRingRadius();
    const cameraAngle = (currentCardIndex / TOTAL_CARDS) * Math.PI * 2;

    renderState.cards.forEach((card, index) => {
      const angle = (index / TOTAL_CARDS) * Math.PI * 2;
      const isCurrent = index === currentCardIndex;

      if (isCurrent) {
        // Selected card: just outside ring, facing camera
        const selectedRadius = ringRadius + 0.5;
        const targetX = Math.sin(cameraAngle) * selectedRadius;
        const targetZ = Math.cos(cameraAngle) * selectedRadius;
        const targetRotY = isFlipped ? cameraAngle + Math.PI : cameraAngle;
        const cardScale = 3 + 2 * getViewportScale(); // Base + proportional (less severe)

        const yOffset = -0.8 - Math.min(getViewportScale(), 1.2) * 0.8; // Lower on larger screens, capped
        damp3(card.mesh.position, [targetX, yOffset, targetZ], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [cardScale, cardScale, cardScale], ANIMATION_SMOOTHING, delta);
        dampE(card.mesh.rotation, [0, targetRotY, 0], ANIMATION_SMOOTHING, delta);
      } else {
        // Other cards: stay on ring
        const targetX = Math.sin(angle) * ringRadius;
        const targetZ = Math.cos(angle) * ringRadius;

        damp3(card.mesh.position, [targetX, -0.1, targetZ], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [0.6, 0.6, 0.6], ANIMATION_SMOOTHING, delta);
        dampE(card.mesh.rotation, [0, angle, 0], ANIMATION_SMOOTHING, delta);
      }
    });
  },

  animateTitle(gameState, titleGroup, camera, delta) {
    // Sticky to top of viewport - position in front of camera
    const distance = 4; // Distance from camera
    const verticalOffset = 1.5; // How high above camera center

    // Get camera's forward direction
    const forward = new THREE.Vector3(0, 0, -1);
    forward.applyQuaternion(camera.quaternion);

    // Position title in front of camera, offset upward
    const targetPos = camera.position.clone()
      .add(forward.multiplyScalar(distance))
      .add(new THREE.Vector3(0, verticalOffset, 0));

    // Subtle bob
    const time = Date.now() * 0.001;
    targetPos.y += Math.sin(time) * 0.02;

    damp3(titleGroup.position, [targetPos.x, targetPos.y, targetPos.z], ANIMATION_SMOOTHING, delta);
    damp3(titleGroup.scale, [0.5, 0.5, 0.5], ANIMATION_SMOOTHING, delta);

    // Face camera (title looks back at camera)
    const camPos = camera.position;
    const lookAngle = Math.atan2(camPos.x - targetPos.x, camPos.z - targetPos.z);
    dampE(titleGroup.rotation, [0, lookAngle, 0], ANIMATION_SMOOTHING, delta);
  },

  handleCardClick(gameState, cardIndex) {
    if (cardIndex === gameState.currentCardIndex) return createFlipAction(gameState.isFlipped);
    return { type: "SELECT_CARD", cardIndex };
  },
};

// =============================================================================
// Free Fly Mode (Original behavior)
// =============================================================================

const freeflyMode: ViewMode = {
  name: "freefly",

  enter(renderState, camera) {
    const config = IS_SMALL_SCREEN
      ? { z: 8, y: 1 }
      : { z: 6.5, y: 1.5 };
    camera.position.set(0, config.y, config.z);
  },

  handleInput(gameState, key) {
    // Navigation handled by WASD in updateCamera
    if (key === " " || key === "Enter" || key === "ArrowUp" || key === "ArrowDown") return createFlipAction(gameState.isFlipped);
    return null;
  },

  updateCamera(gameState, camera, delta) {
    const { keysPressed } = gameState;
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);

    if (keysPressed.w) {
      camera.position.addScaledVector(direction, CAMERA_MOVE_SPEED);
    }
    if (keysPressed.s) {
      camera.position.addScaledVector(direction, -CAMERA_MOVE_SPEED);
    }
    if (keysPressed.a || keysPressed.d) {
      const right = new THREE.Vector3();
      camera.getWorldDirection(right);
      right.cross(camera.up).normalize();
      const multiplier = keysPressed.a ? -CAMERA_MOVE_SPEED : CAMERA_MOVE_SPEED;
      camera.position.addScaledVector(right, multiplier);
    }
    if (keysPressed.q) {
      camera.position.y -= CAMERA_MOVE_SPEED;
    }
    if (keysPressed.e) {
      camera.position.y += CAMERA_MOVE_SPEED;
    }
  },

  animateCards(gameState, renderState, camera, delta) {
    const { currentCardIndex, isFlipped } = gameState;

    renderState.cards.forEach((card, index) => {
      if (index === currentCardIndex) {
        // Selected card flies to camera, scaled up to fill view
        const cameraPosition = camera.position.clone();
        const cameraDirection = new THREE.Vector3();
        camera.getWorldDirection(cameraDirection);

        const fov = camera.fov * (Math.PI / 180);
        const distanceFromCamera = 1 / 2 / Math.tan(fov / 2) + 0.3;
        const targetPosition = cameraPosition.add(
          cameraDirection.multiplyScalar(distanceFromCamera)
        );

        damp3(card.mesh.position, targetPosition, ANIMATION_SMOOTHING, delta);

        // Face camera, flip if needed
        const cardToCamera = new THREE.Vector3();
        cardToCamera.subVectors(camera.position, card.mesh.position);
        const yRotation = Math.atan2(cardToCamera.x, cardToCamera.z);
        const targetRotY = isFlipped ? yRotation + Math.PI : yRotation;

        dampE(card.mesh.rotation, [0, targetRotY, 0], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [1.2, 1.2, 1.2], ANIMATION_SMOOTHING, delta);
      } else {
        // Return to grid position
        damp3(card.mesh.position, card.startPosition, ANIMATION_SMOOTHING, delta);
        dampE(card.mesh.rotation, [0, 0, 0], ANIMATION_SMOOTHING, delta);
        damp3(card.mesh.scale, [1, 1, 1], ANIMATION_SMOOTHING, delta);
      }
    });
  },

  animateTitle(gameState, titleGroup, camera, delta) {
    // Title prominent at top
    const time = Date.now() * 0.001;
    const targetY = 3.5 + Math.sin(time) * 0.02;

    damp3(titleGroup.position, [0, targetY, 1], ANIMATION_SMOOTHING, delta);
    damp3(titleGroup.scale, [1, 1, 1], ANIMATION_SMOOTHING, delta);
    dampE(titleGroup.rotation, [0, 0, 0], ANIMATION_SMOOTHING, delta);
  },

  handleCardClick(gameState, cardIndex) {
    // In freefly, clicking selected card unselects it
    if (cardIndex === gameState.currentCardIndex) return { type: "UNSELECT_CARD" };
    return { type: "SELECT_CARD", cardIndex };
  },
};

// =============================================================================
// Mode Registry
// =============================================================================

const VIEW_MODES: Record<ViewModeName, ViewMode> = {
  carousel: carouselMode,
  orbit: orbitMode,
  freefly: freeflyMode,
};

// =============================================================================
// Game State Management (Immutable)
// =============================================================================

function getUpcomingCardIndex(): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Find first event with date >= today
  const upcomingIndex = calendar.findIndex((entry) => {
    const eventDate = new Date(entry.date);
    return eventDate >= today;
  });

  // If no upcoming event, return last card (most recent past event)
  if (upcomingIndex === -1) {
    return 0; // After reverse, index 0 is the most recent
  }

  // Cards are reversed, so convert index
  return calendar.length - 1 - upcomingIndex;
}

function createInitialGameState(): GameState {
  return {
    viewMode: "carousel",
    currentCardIndex: getUpcomingCardIndex(),
    isFlipped: false,
    keysPressed: {
      w: false, a: false, s: false, d: false, q: false, e: false,
      ArrowLeft: false, ArrowRight: false, ArrowUp: false, ArrowDown: false,
    },
    dragOffset: { x: 0, y: 0 },
    isDragging: false,
  };
}

function gameReducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case "NEXT_CARD":
      return {
        ...state,
        currentCardIndex: (state.currentCardIndex + 1) % TOTAL_CARDS,
        isFlipped: false,
      };

    case "PREV_CARD":
      return {
        ...state,
        currentCardIndex: (state.currentCardIndex - 1 + TOTAL_CARDS) % TOTAL_CARDS,
        isFlipped: false,
      };

    case "SELECT_CARD":
      return {
        ...state,
        currentCardIndex: action.cardIndex,
        isFlipped: false,
      };

    case "FLIP_CARD":
      return { ...state, isFlipped: true };

    case "UNFLIP_CARD":
      return { ...state, isFlipped: false };

    case "UNSELECT_CARD":
      return { ...state, currentCardIndex: -1, isFlipped: false };

    case "SWITCH_MODE":
      return {
        ...state,
        viewMode: action.mode,
        isFlipped: false,
      };

    case "KEY_DOWN": {
      const key = action.key as keyof KeysPressed;
      if (!(key in state.keysPressed)) return state;
      return {
        ...state,
        keysPressed: { ...state.keysPressed, [key]: true },
      };
    }

    case "KEY_UP": {
      const key = action.key as keyof KeysPressed;
      if (!(key in state.keysPressed)) return state;
      return {
        ...state,
        keysPressed: { ...state.keysPressed, [key]: false },
      };
    }

    case "SET_DRAG":
      return {
        ...state,
        dragOffset: { x: action.x, y: action.y },
        isDragging: action.isDragging,
      };

    default:
      return state;
  }
}

// =============================================================================
// Render State
// =============================================================================

function createRenderState(): RenderState {
  return {
    cards: [],
    backgroundCubes: [],
    titleGroup: null,
    hintsGroup: null,
  };
}

// =============================================================================
// Scene Setup
// =============================================================================

function createCamera(): THREE.PerspectiveCamera {
  const aspect = window.innerWidth / window.innerHeight;
  const camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
  camera.position.set(0, 0, 5);
  return camera;
}

function createRenderer(): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer();
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setClearColor("#eee");
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.CineonToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  document.body.appendChild(renderer.domElement);
  return renderer;
}

function createComposer(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera
): { composer: EffectComposer; pencilLinePass: PencilLinesPass } {
  const composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, camera);
  const pencilLinePass = new PencilLinesPass({
    width: window.innerWidth * window.devicePixelRatio,
    height: window.innerHeight * window.devicePixelRatio,
    scene,
    camera,
  });

  composer.addPass(renderPass);
  composer.addPass(pencilLinePass);

  return { composer, pencilLinePass };
}

// =============================================================================
// Scene Object Generators
// =============================================================================

function getRandomCoordinatesInShell(
  outerRadius: number,
  innerRadius: number
): THREE.Vector3 {
  let x: number, y: number, z: number, r: number;

  do {
    const u = Math.random();
    const v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    r = outerRadius * Math.cbrt(Math.random());

    x = r * Math.sin(phi) * Math.cos(theta);
    y = r * Math.sin(phi) * Math.sin(theta);
    z = r * Math.cos(phi);
  } while (r < innerRadius);

  return new THREE.Vector3(x, y, z);
}

function generateBackgroundCubes(
  scene: THREE.Scene,
  count: number,
  outerRadius: number,
  innerRadius: number
): THREE.Mesh[] {
  const geometry = new THREE.BoxGeometry();
  const cubes: THREE.Mesh[] = [];

  for (let i = 0; i < count; i++) {
    const material = new THREE.MeshLambertMaterial({
      color: Math.random() * 0xffffff,
    });
    const cube = new THREE.Mesh(geometry, material);

    const position = getRandomCoordinatesInShell(outerRadius, innerRadius);
    cube.position.copy(position);

    cube.rotation.set(
      Math.random() * 2 * Math.PI,
      Math.random() * 2 * Math.PI,
      Math.random() * 2 * Math.PI
    );

    cube.scale.set(
      Math.random() + 0.8,
      Math.random() + 0.8,
      Math.random() + 0.8
    );

    scene.add(cube);
    cubes.push(cube);
  }

  return cubes;
}

function generateTitle(scene: THREE.Scene): THREE.Group {
  const font = new Font(fontJson);
  const letters = "mosaic".split("");
  const primaryColors = [
    0xc45c5c, // muted red
    0xc9b458, // muted yellow
    0x5c7cc4, // muted blue
  ];

  const group = new THREE.Group();
  let xOffset = 0;

  letters.forEach((letter, index) => {
    const geometry = new TextGeometry(letter, {
      font: font,
      size: 1.2,
      height: 0,
      depth: 0.1,
      curveSegments: 50,
    });

    geometry.computeBoundingBox();
    const color = primaryColors[index % primaryColors.length];
    const material = new THREE.MeshBasicMaterial({ color });
    const mesh = new THREE.Mesh(geometry, material);

    mesh.position.x = xOffset;
    const bbox = geometry.boundingBox!;
    xOffset += bbox.max.x - bbox.min.x + 0.05;

    group.add(mesh);
  });

  // Center the group
  const box = new THREE.Box3().setFromObject(group);
  const center = box.getCenter(new THREE.Vector3());
  group.children.forEach((child) => {
    child.position.x -= center.x;
  });

  group.position.set(0, 3.5, 1);
  scene.add(group);

  return group;
}

function generateHintArrows(scene: THREE.Scene): THREE.Group {
  const font = new Font(fontJson);
  const group = new THREE.Group();

  const hintMaterial = new THREE.MeshBasicMaterial({
    color: 0x333333,
    transparent: true,
    opacity: 0.3
  });

  // Create "swipe < > ^ v" text (font doesn't support unicode arrows)
  const hintText = "swipe  < >  ^ v";
  const textGeometry = new TextGeometry(hintText, {
    font: font,
    size: 0.15,
    height: 0,
    depth: 0.01,
    curveSegments: 12,
  });

  textGeometry.computeBoundingBox();
  const textMesh = new THREE.Mesh(textGeometry, hintMaterial);

  // Center the text
  const bbox = textGeometry.boundingBox!;
  textMesh.position.x = -(bbox.max.x - bbox.min.x) / 2;

  group.add(textMesh);
  group.visible = IS_SMALL_SCREEN; // Only show on mobile

  scene.add(group);
  return group;
}

function generateCards(scene: THREE.Scene): CardRenderData[] {
  const totalCards = calendar.length;
  const cols = IS_SMALL_SCREEN ? 3 : 5;
  const rows = Math.ceil(totalCards / cols);

  const baseCardWidth = 1.1;
  const baseCardHeight = 1.1;
  const baseSpacing = 0.5;

  const cardWidth = IS_SMALL_SCREEN ? baseCardWidth : baseCardWidth * 0.85;
  const cardHeight = IS_SMALL_SCREEN ? baseCardHeight : baseCardHeight * 0.9;
  const spacing = IS_SMALL_SCREEN ? baseSpacing : baseSpacing * 0.8;

  const mobileOffset = IS_SMALL_SCREEN ? -6 : -4;
  const topValue = mobileOffset + (rows * (cardHeight + spacing)) / 2;

  const entries = calendar.map((entry) => entry.card).reverse();
  const loader = new THREE.TextureLoader();
  const cards: CardRenderData[] = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const index = row * cols + col;
      if (index >= totalCards) continue;

      const entry = entries[index];
      const mesh = createCardMesh(loader, entry, cardWidth, cardHeight);

      const x =
        col * (cardWidth + spacing) -
        (cols * (cardWidth + spacing)) / 2 +
        cardWidth / 2;
      const y = topValue - row * (cardHeight + spacing);
      mesh.position.set(x, y, 0);

      scene.add(mesh);
      cards.push({
        mesh,
        startPosition: mesh.position.clone(),
      });
    }
  }

  return cards;
}

function createCardMesh(
  loader: THREE.TextureLoader,
  entry: string,
  width: number,
  height: number
): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(width, height, 0.01);

  const faceTexture = loader.load(
    cardsPng[entry + "_faceout"] || cardsPng[entry + "_titlecard_V2"]
  );
  const backTexture = loader.load(cardsPng[entry + "_titlecard_V2"]);

  if (faceTexture.anisotropy !== undefined) {
    faceTexture.anisotropy = 16;
  }
  if (backTexture.anisotropy !== undefined) {
    backTexture.anisotropy = 16;
  }

  const frontMaterial = new THREE.MeshBasicMaterial({
    map: faceTexture || backTexture,
  });
  const backMaterial = new THREE.MeshBasicMaterial({ map: backTexture });
  const blankMaterial = new THREE.MeshBasicMaterial({ color: 0x0a0508 }); // Very dark with slight tint

  const materials = [
    blankMaterial,
    blankMaterial,
    blankMaterial,
    blankMaterial,
    frontMaterial,
    backMaterial,
  ];

  return new THREE.Mesh(geometry, materials);
}

// =============================================================================
// Background Animation
// =============================================================================

function updateBackgroundCubes(cubes: THREE.Mesh[]): void {
  cubes.forEach((cube) => {
    const pos = cube.position;
    const radius = Math.sqrt(pos.x ** 2 + pos.y ** 2 + pos.z ** 2);
    const theta = Math.acos(pos.z / radius);
    const phi = Math.atan2(pos.y, pos.x) + ORBIT_SPEED;

    cube.position.set(
      radius * Math.sin(theta) * Math.cos(phi),
      radius * Math.sin(theta) * Math.sin(phi),
      radius * Math.cos(theta)
    );
  });
}

// =============================================================================
// Input Handling
// =============================================================================

interface ClientCoords {
  x: number;
  y: number;
}

function getClientCoords(event: MouseEvent | TouchEvent): ClientCoords {
  if (event instanceof MouseEvent) {
    return { x: event.clientX, y: event.clientY };
  }
  const touch = event.touches[0] ?? (event as TouchEvent).changedTouches?.[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : { x: 0, y: 0 };
}

function getPointerPosition(event: MouseEvent | TouchEvent): THREE.Vector2 {
  const { x, y } = getClientCoords(event);
  return new THREE.Vector2(
    (x / window.innerWidth) * 2 - 1,
    -(y / window.innerHeight) * 2 + 1
  );
}

function findIntersectedCardIndex(
  renderState: RenderState,
  camera: THREE.PerspectiveCamera,
  pointer: THREE.Vector2
): number {
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(pointer, camera);

  const meshes = renderState.cards.map((card) => card.mesh);
  const intersects = raycaster.intersectObjects(meshes);

  if (intersects.length === 0) return -1;

  const intersectedMesh = intersects[0].object;
  return renderState.cards.findIndex((card) => card.mesh === intersectedMesh);
}

function handleWindowResize(
  camera: THREE.PerspectiveCamera,
  renderer: THREE.WebGLRenderer,
  composer: EffectComposer,
  pencilLinePass: PencilLinesPass
): void {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  composer.setSize(window.innerWidth, window.innerHeight);
  pencilLinePass.setSize(
    window.innerWidth * window.devicePixelRatio,
    window.innerHeight * window.devicePixelRatio
  );
}

// =============================================================================
// Main Application
// =============================================================================

function initializeApp(): void {
  let gameState: GameState = createInitialGameState();
  const renderState: RenderState = createRenderState();
  const actionQueue: Action[] = [];

  const clock = new THREE.Clock();

  const scene = new THREE.Scene();
  const camera = createCamera();
  const renderer = createRenderer();
  const { composer, pencilLinePass } = createComposer(renderer, scene, camera);

  // Only enable OrbitControls in freefly mode
  const orbitControls = new OrbitControls(camera, renderer.domElement);
  orbitControls.enabled = false;

  // Populate render state
  renderState.backgroundCubes = generateBackgroundCubes(
    scene,
    BACKGROUND_CUBE_CONFIG.count,
    BACKGROUND_CUBE_CONFIG.outerRadius,
    BACKGROUND_CUBE_CONFIG.innerRadius
  );
  renderState.titleGroup = generateTitle(scene);
  renderState.hintsGroup = generateHintArrows(scene);
  renderState.cards = generateCards(scene);

  // Enter initial mode
  VIEW_MODES[gameState.viewMode].enter(renderState, camera);

  // Track drag state
  let dragStart: { x: number; y: number } | null = null;

  // =========================================================================
  // Game Loop
  // =========================================================================
  const GAME_TICK_MS = 1000 / 60;
  setInterval(() => {
    while (actionQueue.length > 0) {
      const action = actionQueue.shift()!;
      const prevMode = gameState.viewMode;
      gameState = gameReducer(gameState, action);

      // Handle mode transitions
      if (action.type === "SWITCH_MODE" && action.mode !== prevMode) {
        VIEW_MODES[action.mode].enter(renderState, camera);
        orbitControls.enabled = action.mode === "freefly";
      }
    }
  }, GAME_TICK_MS);

  // =========================================================================
  // Render Loop
  // =========================================================================
  let prevMode: ViewModeName = gameState.viewMode;
  let hintOpacity = 0.3;

  function render(): void {
    const delta = clock.getDelta();
    const mode = VIEW_MODES[gameState.viewMode];

    mode.updateCamera(gameState, camera, delta);
    mode.animateCards(gameState, renderState, camera, delta);

    if (renderState.titleGroup) {
      mode.animateTitle(gameState, renderState.titleGroup, camera, delta);
    }

    // Animate hints at bottom of viewport
    if (renderState.hintsGroup && IS_SMALL_SCREEN) {
      const distance = 3;
      const verticalOffset = -1.3;

      // Get camera's forward direction
      const forward = new THREE.Vector3(0, 0, -1);
      forward.applyQuaternion(camera.quaternion);

      // Position hints in front of camera, offset downward
      const targetPos = camera.position.clone()
        .add(forward.multiplyScalar(distance))
        .add(new THREE.Vector3(0, verticalOffset, 0));

      damp3(renderState.hintsGroup.position, [targetPos.x, targetPos.y, targetPos.z], ANIMATION_SMOOTHING, delta);

      // Face camera
      const camPos = camera.position;
      const lookAngle = Math.atan2(camPos.x - targetPos.x, camPos.z - targetPos.z);
      dampE(renderState.hintsGroup.rotation, [0, lookAngle, 0], ANIMATION_SMOOTHING, delta);

      // Fade during mode transitions
      const targetOpacity = gameState.viewMode !== prevMode ? 0 : 0.3;
      hintOpacity += (targetOpacity - hintOpacity) * 0.1;
      const material = (renderState.hintsGroup.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial;
      material.opacity = hintOpacity;

      if (gameState.viewMode === prevMode) {
        prevMode = gameState.viewMode;
      }
    }

    // Track mode changes for fade effect
    prevMode = gameState.viewMode;

    updateBackgroundCubes(renderState.backgroundCubes);

    composer.render();
    requestAnimationFrame(render);
  }

  // =========================================================================
  // Input Handlers
  // =========================================================================
  let lastTapTime = 0;

  function handlePointerDown(event: MouseEvent | TouchEvent): void {
    dragStart = getClientCoords(event);

    // Double-tap detection for view switching (only on background, not cards)
    if (event instanceof TouchEvent) {
      const pointer = getPointerPosition(event);
      const cardIndex = findIntersectedCardIndex(renderState, camera, pointer);
      const tappedOnCard = cardIndex >= 0;

      if (!tappedOnCard) {
        const now = Date.now();
        if (now - lastTapTime < 300) {
          // Double tap on background - cycle views (no freefly on mobile)
          const modes: ViewModeName[] = ["carousel", "orbit"];
          const currentIndex = modes.indexOf(gameState.viewMode);
          const nextMode = modes[(currentIndex + 1) % modes.length];
          actionQueue.push({ type: "SWITCH_MODE", mode: nextMode });
          lastTapTime = 0; // Reset to prevent triple-tap
        } else {
          lastTapTime = now;
        }
      }
    }
  }

  function handlePointerMove(event: MouseEvent | TouchEvent): void {
    if (!dragStart) return;

    // Prevent scrolling on touch
    if (event instanceof TouchEvent) {
      event.preventDefault();
    }

    const coords = getClientCoords(event);
    const dx = (coords.x - dragStart.x) / window.innerWidth;
    const dy = (coords.y - dragStart.y) / window.innerHeight;

    actionQueue.push({ type: "SET_DRAG", x: dx, y: dy, isDragging: true });
  }

  function handlePointerUp(event: MouseEvent | TouchEvent): void {
    if (dragStart) {
      const coords = getClientCoords(event);
      const dx = coords.x - dragStart.x;
      const dy = coords.y - dragStart.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Swipe detection for mobile navigation
      const swipeThreshold = 50;
      const tapThreshold = 25; // Larger threshold for mobile touch

      if (absDx > swipeThreshold && absDx > absDy * 1.5) {
        // Horizontal swipe - navigate cards
        if (dx > 0) {
          actionQueue.push({ type: "PREV_CARD" });
        } else {
          actionQueue.push({ type: "NEXT_CARD" });
        }
      } else if (absDy > swipeThreshold && absDy > absDx * 1.5) {
        // Vertical swipe - flip card
        actionQueue.push(gameState.isFlipped ? { type: "UNFLIP_CARD" } : { type: "FLIP_CARD" });
      } else if (absDx < tapThreshold && absDy < tapThreshold) {
        // Tap - handle card interaction
        const pointer = getPointerPosition(event);
        const cardIndex = findIntersectedCardIndex(renderState, camera, pointer);

        if (cardIndex >= 0) {
          const mode = VIEW_MODES[gameState.viewMode];
          const action = mode.handleCardClick(gameState, cardIndex);
          if (action) actionQueue.push(action);
        }
      }

      dragStart = null;
      actionQueue.push({ type: "SET_DRAG", x: 0, y: 0, isDragging: false });
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    // Mode switching
    if (MODE_KEYS[event.key]) {
      actionQueue.push({ type: "SWITCH_MODE", mode: MODE_KEYS[event.key] });
      return;
    }

    // Mode-specific input
    const mode = VIEW_MODES[gameState.viewMode];
    const action = mode.handleInput(gameState, event.key);
    if (action) {
      actionQueue.push(action);
    }

    // Always track key state for freefly mode
    actionQueue.push({ type: "KEY_DOWN", key: event.key });
  }

  function handleKeyUp(event: KeyboardEvent): void {
    actionQueue.push({ type: "KEY_UP", key: event.key });
  }

  // Bind events
  window.addEventListener("mousedown", handlePointerDown);
  window.addEventListener("mousemove", handlePointerMove);
  window.addEventListener("mouseup", handlePointerUp);
  window.addEventListener("touchstart", handlePointerDown, { passive: true });
  window.addEventListener("touchmove", handlePointerMove, { passive: false });
  window.addEventListener("touchend", handlePointerUp);
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);
  window.addEventListener("resize", () => handleWindowResize(camera, renderer, composer, pencilLinePass));

  // Start render loop
  render();

  // Log mode instructions
  console.log("View Modes: 1=Carousel, 2=Orbit, 3=FreeFly");
  console.log("Navigation: Arrow keys, Space/Enter to flip");
}

initializeApp();
